// Mirrors Whatsapp::Providers::BaseService::INTERACTIVE_LIMITS. The backend still
// truncates and caps, so these only exist to warn the agent before they send.
export const MAX_BUTTONS = 3;
export const MAX_LIST_ROWS = 10;
export const MAX_BUTTON_TITLE = 20;
export const MAX_ROW_TITLE = 24;
export const MAX_HEADER_TEXT = 60;
export const MAX_FOOTER_TEXT = 60;
export const MAX_BODY_TEXT = 1024;

export const PRESENTATION_AUTO = 'auto';
export const PRESENTATION_BUTTON = 'button';
export const PRESENTATION_LIST = 'list';

/**
 * Resolves how a set of options will actually be rendered by WhatsApp. Keep this in
 * step with `interactive_message_type` in the Ruby base provider: an explicit choice
 * wins, a section label forces a list, and otherwise the option count decides.
 * @param {string} presentation one of auto | button | list
 * @param {Array} options
 * @returns {string} 'button' or 'list'
 */
export const resolvePresentation = (presentation, options = []) => {
  if (presentation === PRESENTATION_BUTTON) return PRESENTATION_BUTTON;
  if (presentation === PRESENTATION_LIST) return PRESENTATION_LIST;
  if (options.some(option => option.section)) return PRESENTATION_LIST;
  return options.length <= MAX_BUTTONS
    ? PRESENTATION_BUTTON
    : PRESENTATION_LIST;
};

/**
 * Builds the content_attributes an input_select message needs. Options are sent flat;
 * the backend groups them into sections when a section label is present.
 */
export const buildOptionsAttributes = ({
  options,
  presentation,
  header,
  footer,
  listButtonLabel,
}) => {
  const resolved = resolvePresentation(presentation, options);
  const attributes = {
    items: options.map(option => {
      const item = {
        title: option.title.trim(),
        // WhatsApp requires an id per option; fall back to the title so an agent
        // never has to invent one.
        value: (option.value || option.title).trim(),
      };
      if (resolved === PRESENTATION_LIST && option.description) {
        item.description = option.description.trim();
      }
      if (resolved === PRESENTATION_LIST && option.section) {
        item.section = option.section.trim();
      }
      return item;
    }),
  };

  if (presentation !== PRESENTATION_AUTO) {
    attributes.interactive_type = resolved;
  }
  if (header) attributes.header = header.trim();
  if (footer) attributes.footer = footer.trim();
  if (resolved === PRESENTATION_LIST && listButtonLabel) {
    attributes.list_button_label = listButtonLabel.trim();
  }
  return attributes;
};

/**
 * Builds the content_attributes for an interactive flow message.
 */
export const buildFlowAttributes = ({
  flowId,
  cta,
  header,
  footer,
  screen,
  screenData,
}) => {
  const attributes = { flow_id: String(flowId) };
  if (cta) attributes.flow_cta = cta.trim();
  if (header) attributes.header = header.trim();
  if (footer) attributes.footer = footer.trim();
  if (screen) {
    attributes.flow_action = 'navigate';
    attributes.flow_action_screen = screen.trim();
    if (screenData) attributes.flow_action_data = screenData;
  }
  return attributes;
};
